import type { CloudInstanceDetailData } from 'components/DeviceView/CloudConstructView/CloudInstanceDetailContent';
import type { CloudInternetGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudInternetGatewayDetailContent';
import type { CloudNatGatewayDetailData } from 'components/DeviceView/CloudConstructView/CloudNatGatewayDetailContent';
import type { CloudNetworkDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkDetailContent';
import type { CloudNetworkInterfaceDetailData } from 'components/DeviceView/CloudConstructView/CloudNetworkInterfaceDetailContent';
import type { CloudPeeringDetailData } from 'components/DeviceView/CloudConstructView/CloudPeeringDetailContent';
import type { CloudSubnetDetailData } from 'components/DeviceView/CloudConstructView/CloudSubnetDetailContent';
import type {
  ConfigurationData,
  DeviceViewEntityId,
} from 'components/DeviceView/deviceViewTypes';
import type { DeviceDetailData } from 'containers/HostnameDetail/HostnameDetailContent';
import type { DeviceViewCommandFactory } from '../DeviceViewModel';

interface SetTabDataCommand {
  deviceViewEntityId: DeviceViewEntityId;
  deviceData?: DeviceDetailData;
  cloudNetworkData?: CloudNetworkDetailData;
  cloudNetworkInterfaceData?: CloudNetworkInterfaceDetailData;
  cloudSubnetData?: CloudSubnetDetailData;
  cloudPeeringData?: CloudPeeringDetailData;
  cloudInstanceData?: CloudInstanceDetailData;
  cloudNatGatewayData?: CloudNatGatewayDetailData;
  cloudInternetGatewayData?: CloudInternetGatewayDetailData;
  configurationData?: ConfigurationData;
}

export const setTabData: DeviceViewCommandFactory<SetTabDataCommand> =
  (cmdContext) => (command) => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable
    cmdContext.updateState((draft) => {
      draft.tabData[command.deviceViewEntityId] = {};
      if (command.deviceData) {
        draft.tabData[command.deviceViewEntityId]!.device = {
          configReg: command.deviceData.configReg ?? null,
          family: command.deviceData.family ?? null,
          hostname: command.deviceData.hostname ?? null,
          image: command.deviceData.image ?? null,
          loginIpv4: command.deviceData.loginIpv4 ?? null,
          loginIpv6: command.deviceData.loginIpv6 ?? null,
          loginPort: command.deviceData.loginPort ?? null,
          loginType: command.deviceData.loginType ?? null,
          memoryUtilization: command.deviceData.memoryUtilization ?? null,
          model: command.deviceData.model ?? null,
          platform: command.deviceData.platform ?? null,
          processor: command.deviceData.processor ?? null,
          rd: command.deviceData.rd ?? null,
          reload: command.deviceData.reload ?? null,
          siteName: command.deviceData.siteName ?? null,
          sn: command.deviceData.sn ?? null,
          stpDomain: command.deviceData.stpDomain ?? null,
          taskKey: command.deviceData.taskKey ?? null,
          uptime: command.deviceData.uptime,
          vendor: command.deviceData.vendor ?? null,
          version: command.deviceData.version ?? null,
          slug: command.deviceData.slug ?? null,
        };
      }
      if (command.cloudNetworkData) {
        draft.tabData[command.deviceViewEntityId]!.cloudNetwork = {
          cloudProvider: command.cloudNetworkData.cloudProvider ?? null,
          networkId: command.cloudNetworkData.networkId ?? null,
          name: command.cloudNetworkData.name ?? null,
          resourceName: command.cloudNetworkData.resourceName ?? null,
          state: command.cloudNetworkData.state ?? null,
          region: command.cloudNetworkData.region ?? null,
          ipv4CidrBlocks: command.cloudNetworkData.ipv4CidrBlocks ?? null,
          ipv6CidrBlocks: command.cloudNetworkData.ipv6CidrBlocks ?? null,
          peeringCount: command.cloudNetworkData.peeringCount ?? null,
        };
      }
      if (command.cloudNetworkInterfaceData) {
        draft.tabData[command.deviceViewEntityId]!.cloudNetworkInterface = {
          cloudProvider:
            command.cloudNetworkInterfaceData.cloudProvider ?? null,
          networkInterfaceId:
            command.cloudNetworkInterfaceData.networkInterfaceId ?? null,
          cloudNetworkId:
            command.cloudNetworkInterfaceData.cloudNetworkId ?? null,
          subnet: command.cloudNetworkInterfaceData.subnet ?? null,
          description: command.cloudNetworkInterfaceData.description ?? null,
          availabilityZone:
            command.cloudNetworkInterfaceData.availabilityZone ?? null,
        };
      }
      if (command.cloudSubnetData) {
        draft.tabData[command.deviceViewEntityId]!.cloudSubnet = {
          cloudProvider: command.cloudSubnetData.cloudProvider ?? null,
          subnetId: command.cloudSubnetData.subnetId ?? null,
          cloudNetworkId: command.cloudSubnetData.cloudNetworkId ?? null,
          name: command.cloudSubnetData.name ?? null,
          availabilityZone: command.cloudSubnetData.availabilityZone ?? null,
          region: command.cloudSubnetData.region ?? null,
        };
      }
      if (command.cloudPeeringData) {
        draft.tabData[command.deviceViewEntityId]!.cloudPeering = {
          cloudProvider: command.cloudPeeringData.cloudProvider ?? null,
          peeringId: command.cloudPeeringData.peeringId ?? null,
          cloudNetworkId: command.cloudPeeringData.cloudNetworkId ?? null,
          remoteNetworkId: command.cloudPeeringData.remoteNetworkId ?? null,
          name: command.cloudPeeringData.name ?? null,
        };
      }
      if (command.cloudInstanceData) {
        draft.tabData[command.deviceViewEntityId]!.cloudInstance = {
          cloudProvider: command.cloudInstanceData.cloudProvider ?? null,
          vmId: command.cloudInstanceData.vmId ?? null,
          name: command.cloudInstanceData.name ?? null,
          status: command.cloudInstanceData.status ?? null,
          os: command.cloudInstanceData.os ?? null,
          image: command.cloudInstanceData.image ?? null,
          availabilityZone: command.cloudInstanceData.availabilityZone ?? null,
        };
      }
      if (command.cloudNatGatewayData) {
        draft.tabData[command.deviceViewEntityId]!.cloudNatGateway = {
          cloudProvider: command.cloudNatGatewayData.cloudProvider ?? null,
          gatewayId: command.cloudNatGatewayData.gatewayId ?? null,
          vpcId: command.cloudNatGatewayData.vpcId ?? null,
          name: command.cloudNatGatewayData.name ?? null,
          region: command.cloudNatGatewayData.region ?? null,
          availabilityMode:
            command.cloudNatGatewayData.availabilityMode ?? null,
          connectivityType:
            command.cloudNatGatewayData.connectivityType ?? null,
          sku: command.cloudNatGatewayData.sku ?? null,
          awsArn: command.cloudNatGatewayData.awsArn ?? null,
          availabilityZoneList:
            command.cloudNatGatewayData.availabilityZoneList ?? null,
          routeTableId: command.cloudNatGatewayData.routeTableId ?? null,
          cloudRouterId: command.cloudNatGatewayData.cloudRouterId ?? null,
          publicIpv4List: command.cloudNatGatewayData.publicIpv4List ?? null,
          publicIpv6List: command.cloudNatGatewayData.publicIpv6List ?? null,
        };
      }
      if (command.cloudInternetGatewayData) {
        draft.tabData[command.deviceViewEntityId]!.cloudInternetGateway = {
          gatewayId: command.cloudInternetGatewayData.gatewayId ?? null,
          vpcId: command.cloudInternetGatewayData.vpcId ?? null,
          name: command.cloudInternetGatewayData.name ?? null,
          type: command.cloudInternetGatewayData.type ?? null,
          arn: command.cloudInternetGatewayData.arn ?? null,
        };
      }
      if (command.configurationData) {
        draft.tabData[command.deviceViewEntityId]!.configuration = {
          hash: command.configurationData?.hash ?? null,
          hostname: command.configurationData?.hostname ?? null,
          lastChangeAt: command.configurationData?.lastChangeAt ?? null,
          sn: command.configurationData?.sn ?? null,
        };
      }
    });
  };
